import { AlertCircle } from 'lucide-react';
import { Badge } from '@/components/atoms/badge';
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@/components/atoms/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/atoms/tooltip';
import { cn } from '@/lib/utils';

interface TMStatusBadgeProps {
  label: 'Dynamic' | 'Dynamic override' | null;
  warning?: string | null;
  laborRate?: number | null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

export function TMStatusBadge({ label, warning, laborRate }: TMStatusBadgeProps) {
  if (!label) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Popover>
        <PopoverTrigger asChild>
          <Badge
            variant="outline"
            className={cn(
              'cursor-pointer',
              label === 'Dynamic override'
                ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100',
            )}
          >
            {label}
          </Badge>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-64 p-3">
          <PopoverHeader className="gap-1">
            <PopoverTitle>{label}</PopoverTitle>
            <PopoverDescription className="text-xs leading-5">
              {label === 'Dynamic override'
                ? 'This estimate is pricing this service dynamically instead of the flat price its pricebook entry sets.'
                : 'This service is set to dynamic pricing in the pricebook.'}
            </PopoverDescription>
          </PopoverHeader>
          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
            <p>Price = (Hours x Labor Rate) + (Material/Equipment Cost x Markup) + surcharges.</p>
            <p>The Price field is not used while a service is priced dynamically.</p>
            {/* Only a line still waiting to be derived from one hourly rate has
                one to state. A materialized dynamic service keeps its rates in
                its labor lines, and printing the empty field as "$0.00/hr"
                claims a missing rate the service does not need. */}
            {(laborRate ?? 0) > 0 ? (
              <p>
                Labor Rate:{' '}
                <span className="font-medium text-foreground">
                  {formatCurrency(laborRate ?? 0)}/hr
                </span>
              </p>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>

      {warning && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-amber-50 text-amber-700 transition-colors hover:bg-amber-100"
              aria-label={warning}
            >
              <AlertCircle className="h-3 w-3" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{warning}</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
